import { useState, useCallback } from 'react';
import {
  getMyTeam,
  getTeamById,
  getTeamTokenUsage,
  getTeamUsageOverview,
  getTeamMembersUsage,
  getMemberUsage,
  getTeamUsageHistory,
  updateTeam,
  regenerateInviteCode,
  removeMember,
  sendInvites,
  leaveTeam,
  updateSeats,
  getActivityLog
} from '../../apiLayer/dashBoardAPIs/teamDashboardAPIs';

export const useTeamDashboard = () => {
  // Team info state
  const [teamInfo, setTeamInfo] = useState(null);
  const [members, setMembers] = useState([]);

  // Usage state
  const [teamUsage, setTeamUsage] = useState(null);
  const [membersUsage, setMembersUsage] = useState([]);
  const [usageHistory, setUsageHistory] = useState([]);
  const [selectedMemberUsage, setSelectedMemberUsage] = useState(null);

  // Activity state
  const [activityLog, setActivityLog] = useState([]);
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [activityTotal, setActivityTotal] = useState(0);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ===================
  // TEAM INFO METHODS
  // ===================

  // Fetch current user's team
  const fetchMyTeam = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getMyTeam();
      if (data) {
        setTeamInfo(data);
        setMembers(data.members || []);
      }
      return data;
    } catch (err) {
      console.error('Error fetching my team:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch team by ID
  const fetchTeamById = useCallback(async (teamId) => {
    try {
      setLoading(true);
      const data = await getTeamById(teamId);
      if (data) {
        setTeamInfo(data);
        setMembers(data.members || []);
      }
      return data;
    } catch (err) {
      console.error('Error fetching team by ID:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ====================
  // TOKEN USAGE METHODS
  // ====================

  // Fetch team token usage (using familiar format)
  const fetchTeamTokenUsage = useCallback(async (teamId, params = {}) => {
    try {
      const defaultParams = {
        data_type: 'month',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        ...params
      };
      const data = await getTeamTokenUsage(teamId, defaultParams);
      return data;
    } catch (err) {
      console.error('Error fetching team token usage:', err);
      setError(err.message);
      return null;
    }
  }, []);

  // Fetch team usage overview
  const fetchTeamUsageOverview = useCallback(async (teamId, period = 'month') => {
    try {
      const data = await getTeamUsageOverview(teamId, period);
      setTeamUsage(data);
      return data;
    } catch (err) {
      console.error('Error fetching team usage overview:', err);
      setError(err.message);
      return null;
    }
  }, []);

  // Fetch all members' usage
  const fetchMembersUsage = useCallback(async (teamId, period = 'month') => {
    try {
      const data = await getTeamMembersUsage(teamId, period);
      if (data && data.members) {
        setMembersUsage(data.members);
      }
      return data;
    } catch (err) {
      console.error('Error fetching members usage:', err);
      setError(err.message);
      return null;
    }
  }, []);

  // Fetch single member usage
  const fetchMemberUsage = useCallback(async (teamId, userId, period = 'month') => {
    try {
      const data = await getMemberUsage(teamId, userId, period);
      setSelectedMemberUsage(data);
      return data;
    } catch (err) {
      console.error('Error fetching member usage:', err);
      setError(err.message);
      return null;
    }
  }, []);

  // Fetch usage history for charts
  const fetchUsageHistory = useCallback(async (teamId, granularity = 'day', range = '30d') => {
    try {
      const data = await getTeamUsageHistory(teamId, granularity, range);
      if (data && data.data_points) {
        // Transform to chart format
        const chartData = data.data_points.map(point => ({
          key: point.date,
          value: point.total_tokens
        }));
        setUsageHistory(chartData);
      }
      return data;
    } catch (err) {
      console.error('Error fetching usage history:', err);
      setError(err.message);
      return null;
    }
  }, []);

  // ==========================
  // TEAM MANAGEMENT METHODS
  // ==========================

  // Update team details
  const updateTeamDetails = useCallback(async (teamId, data) => {
    try {
      setLoading(true);
      const result = await updateTeam(teamId, data);
      // Refresh team info after update
      if (result) {
        await fetchTeamById(teamId);
      }
      return result;
    } catch (err) {
      console.error('Error updating team:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchTeamById]);

  // Regenerate invite code
  const regenerateInvite = useCallback(async (teamId) => {
    try {
      setLoading(true);
      const result = await regenerateInviteCode(teamId);
      // Update local state with new invite info
      if (result && teamInfo) {
        setTeamInfo({
          ...teamInfo,
          invite_code: result.invite_code,
          invite_link: result.invite_link
        });
      }
      return result;
    } catch (err) {
      console.error('Error regenerating invite code:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [teamInfo]);

  // Remove a member
  const removeTeamMember = useCallback(async (teamId, userId) => {
    try {
      setLoading(true);
      const result = await removeMember(teamId, userId);
      // Remove member from local state
      if (result) {
        setMembers(prev => prev.filter(m => m.user_id !== userId));
        // Update seat count
        if (teamInfo) {
          setTeamInfo({
            ...teamInfo,
            used_seats: teamInfo.used_seats - 1,
            available_seats: teamInfo.available_seats + 1
          });
        }
      }
      return result;
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [teamInfo]);

  // Send invites
  const sendTeamInvites = useCallback(async (teamId, emails, inviteLink = null) => {
    try {
      setLoading(true);
      const result = await sendInvites(teamId, emails, inviteLink);
      return result;
    } catch (err) {
      console.error('Error sending invites:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Leave team (for members)
  const leaveCurrentTeam = useCallback(async () => {
    try {
      setLoading(true);
      const result = await leaveTeam();
      // Clear team state after leaving
      if (result) {
        setTeamInfo(null);
        setMembers([]);
      }
      return result;
    } catch (err) {
      console.error('Error leaving team:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update seats
  const addMoreSeats = useCallback(async (additionalSeats) => {
    try {
      setLoading(true);
      const result = await updateSeats(additionalSeats);
      // Update local state with new seat count
      if (result && teamInfo) {
        setTeamInfo({
          ...teamInfo,
          total_seats: result.new_total_seats
        });
      }
      return result;
    } catch (err) {
      console.error('Error adding seats:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [teamInfo]);

  // =====================
  // ACTIVITY LOG METHODS
  // =====================

  // Fetch activity log
  const fetchActivityLog = useCallback(async (teamId, limit = 50, offset = 0) => {
    try {
      const data = await getActivityLog(teamId, limit, offset);
      if (data) {
        if (offset === 0) {
          setActivityLog(data.activities || []);
        } else {
          // Append for pagination
          setActivityLog(prev => [...prev, ...(data.activities || [])]);
        }
        setActivityHasMore(data.has_more || false);
        setActivityTotal(data.total_count || 0);
      }
      return data;
    } catch (err) {
      console.error('Error fetching activity log:', err);
      setError(err.message);
      return null;
    }
  }, []);

  // Load more activities (pagination)
  const loadMoreActivities = useCallback(async (teamId) => {
    const currentOffset = activityLog.length;
    return fetchActivityLog(teamId, 50, currentOffset);
  }, [activityLog.length, fetchActivityLog]);

  // ====================
  // INITIALIZATION
  // ====================

  // Initialize team dashboard (fetch all needed data)
  const initializeTeamDashboard = useCallback(async (teamId) => {
    setLoading(true);
    setError(null);

    try {
      // Fetch team info first
      const team = await fetchTeamById(teamId);

      if (team) {
        // Fetch usage data in parallel
        await Promise.all([
          fetchTeamUsageOverview(teamId, 'month'),
          fetchMembersUsage(teamId, 'month'),
          fetchUsageHistory(teamId, 'day', '30d'),
          fetchActivityLog(teamId, 50, 0)
        ]);
      }

      return team;
    } catch (err) {
      setError('Failed to initialize team dashboard');
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchTeamById, fetchTeamUsageOverview, fetchMembersUsage, fetchUsageHistory, fetchActivityLog]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    teamInfo,
    members,
    teamUsage,
    membersUsage,
    usageHistory,
    selectedMemberUsage,
    activityLog,
    activityHasMore,
    activityTotal,
    loading,
    error,

    // Team info methods
    fetchMyTeam,
    fetchTeamById,

    // Usage methods
    fetchTeamTokenUsage,
    fetchTeamUsageOverview,
    fetchMembersUsage,
    fetchMemberUsage,
    fetchUsageHistory,

    // Management methods
    updateTeamDetails,
    regenerateInvite,
    removeTeamMember,
    sendTeamInvites,
    leaveCurrentTeam,
    addMoreSeats,

    // Activity methods
    fetchActivityLog,
    loadMoreActivities,

    // Initialization
    initializeTeamDashboard,
    clearError
  };
};
